import React, { useState, useEffect, useCallback, useRef } from "react";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  ArcElement,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";

import { getLanguageString as _ } from "misc/lang";
import { getLanguageStringArray as _ar } from "misc/lang";
import { classNames } from "misc/helpers";

import { DefaultProps } from "model/props";
import {
  apiGetAccountStats,
  apiGetHomeStats,
  apiGetUserStats,
  apiGetStaffStatsHome,
  apiGetStaffStatsUser,
  apiGetUserCalendarHistory,
  apiGetWellbeingHistory,
  apiGetAccountList,
} from "net/requests";

import cssLayout from "../layout.module.scss";
import css from "./statsscreen.module.scss";

import {
  processActivityStats,
  getStatus,
  processMonthlyStats,
  processStaffMonthlyStats,
  getObjectiveColor,
  processUserWellbeing,
} from "misc/stats_util";
import TwoGraph from "components/stats/TwoGraph";
import BoxPercent from "components/stats/BoxPercent";
import BoxSignOff from "components/stats/BoxSignOff";

import {
  Stats,
  ActivityStats,
  HomeStats,
  ObjectiveStatsAndMergedSummary,
  StatsAndMergedSummary,
  MonthlyStatsAndSummary,
  UserStats,
  StaffSignOffStats,
  StaffMap,
  ActivityMap,
  Wellbeing as WellbeingData,
  WellbeingSummary,
} from "model/stats";
import { CalendarActivity } from "model/calendar";
import FormButton from "components/form/FormButton";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { COLOR_PALETTE, COLOR_GRAY, Paths } from "appConfig";
import ActivityTable from "components/stats/ActivityTable";
import { FAKE_WELLBEING } from "net/fake_respons";
import WellbeingCalendar from "components/wellbeing/WellbeingCalendar";
import Wellbeing from "components/wellbeing/Wellbeing";
import { Organisation } from "model/organisation";
import PageHeader from "components/pageheader/PageHeader";

const StatsScreen: React.FC<DefaultProps> = (props) => {
  const {
    admin,
    account,
    setAccount,
    stats: propsStats,
    setStats: propsSetStats,
    loader,
    globalAppMode,
  } = props;

  const [localStats, setLocalStats] = useState<StatsAndMergedSummary>();
  const [organizations, setOrganizations] = useState<Organisation[]>([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgError, setOrgError] = useState<string>();
  const [activeTab, setActiveTab] = useState(0);

  const stats = localStats || propsStats;

  ChartJS.register(
    CategoryScale,
    ArcElement,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Tooltip,
    Legend,
  );

  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [period, setPeriod] = useState<number>(new Date().getMonth());
  const [homeStats, setHomeStats] = useState<StatsAndMergedSummary>();
  const [staffStatsHome, setStaffStatsHome] = useState<StatsAndMergedSummary>();
  const [staffMap, setStaffMap] = useState<StaffMap>();
  const [staffStatsUser, setStaffStatsUser] = useState<StatsAndMergedSummary>();
  const [selectedHome, setSelectedHome] = useState<HomeStats>();
  const [selectedUser, setSelectedUser] = useState<UserStats>();
  const [userWellbeing, setUserWellbeing] = useState<WellbeingData[][][]>();
  const [userWellbeingYearSummary, setUserWellbeingYearSummary] =
    useState<WellbeingSummary>();

  const [objectives, setObjectives] =
    useState<ObjectiveStatsAndMergedSummary[]>();
  const [activityMap, setActivityMap] = useState<ActivityMap>();
  const [selectedObjective, setSelectedObjective] =
    useState<ObjectiveStatsAndMergedSummary>();
  const [lineChartData, setLineChartData] = useState<any>();
  const [barChartData, setBarChartData] = useState<any>();
  const [userCalendarHistory, setUserCalendarHistory] =
    useState<CalendarActivity[][]>();

  const ref = useRef<ChartJS<"bar">>(null);

  const loadOrganizations = useCallback(() => {
    if (!admin || !admin.is_super_admin || globalAppMode !== "HEALTH") return;

    setOrgLoading(true);
    setOrgError(undefined);

    apiGetAccountList(admin, globalAppMode)
      .then((response) => {
        const orgs: Organisation[] = response.data;
        setOrganizations(orgs.filter((org) => !org.parent_uuid));
      })
      .catch(() => {
        setOrgError(_("stats", "error_loading_organizations"));
      })
      .finally(() => {
        setOrgLoading(false);
      });
  }, [admin, globalAppMode]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  /////////////////////////////////////////////////////////////////////////
  useEffect(() => {
    setPeriod(-1);
    setSelectedUser(undefined);

    // eslint-disable-next-line
  }, [year]);

  useEffect(() => {
    actionLoadAccountStats();

    // eslint-disable-next-line
  }, [account, year]);

  useEffect(() => {
    setHomeStats(undefined);
    if (selectedHome?.home?.uuid) actionLoadHomeStats(selectedHome.home.uuid);

    // eslint-disable-next-line
  }, [selectedHome, year]);

  useEffect(() => {
    setObjectives(undefined);
    setSelectedObjective(undefined);
    setUserWellbeing(undefined);
    if (selectedUser?.user?.uuid)
      actionLoadUserStats(
        selectedUser.user.uuid,
        selectedHome?.home.uuid || "",
      );

    // eslint-disable-next-line
  }, [selectedUser, year]);

  useEffect(() => {
    if (!selectedObjective) return;

    const datasets = [];

    if (period < 0) {
      // FULL YEAR

      // BARS
      for (let i = 0; i < selectedObjective.stats.length; i++) {
        const item = selectedObjective.stats[i];
        datasets.push({
          data: item.months.map((m) => m.count_performed),
          //fill: false,
          //tension: 0.1,
          backgroundColor: COLOR_PALETTE[i],
          stack: "stack-" + i,
        });
        datasets.push({
          data: item.months.map((m) => m.count_total - m.count_performed),
          //fill: false,
          //tension: 0.1,
          backgroundColor: COLOR_GRAY,
          stack: "stack-" + i,
        });
      }
      const data = {
        labels: _ar("general", "month_names"),
        datasets: datasets,
      };
      setBarChartData(data);
    } else {
      // BAR
      const labels: string[] = [];
      const colors: string[] = [];

      const data_performed = [];
      const data_total = [];

      for (let i = 0; i < selectedObjective.stats.length; i++) {
        const item = selectedObjective.stats[i] as ActivityStats;
        labels.push(item.activity_title);
        colors.push(COLOR_PALETTE[i]);
        const m = item.months[period];
        data_performed.push(m.count_performed);
        data_total.push(m.count_total - m.count_performed);
      }

      datasets.push({
        data: data_performed,
        backgroundColor: colors,
        stack: "stack0",
      });
      datasets.push({
        data: data_total,
        backgroundColor: COLOR_GRAY,
        stack: "stack0",
      });

      const data = {
        labels: labels,
        datasets: datasets,
      };
      setBarChartData(data);
    }

    // eslint-disable-next-line
  }, [selectedObjective, period]);

  /////////////////////////////////////////////////////////////////////////

  const actionLoadAccountStats = () => {
    if (!account) return;
    loader.setLoading();

    apiGetAccountStats(admin, account.uuid, year)
      .then((response) => {
        console.log("account stats response", response.data);
        loader.reset();
        const summary = processMonthlyStats(response.data);
        console.log("account stats summary", summary);
        setLocalStats(summary);
        if (propsSetStats) propsSetStats(summary);

        const notSingle = summary.stats.length !== 1;
        if (notSingle) {
          setSelectedHome(undefined);
        } else {
          setSelectedHome(summary.stats[0] as HomeStats);
        }
      })
      .catch((error) => {
        loader.reset();
        const errorCode = error.response?.data?.error;
        console.log("account stats error", errorCode, error);
      });
  };

  const actionLoadHomeStats = (home_uuid: string) => {
    if (!account) return;
    loader.setLoading(2);

    apiGetHomeStats(admin, home_uuid, year)
      .then((response) => {
        console.log("home stats response", response.data);
        loader.resetOnZero();
        const merged = processMonthlyStats(response.data);
        console.log("home stats merged", merged);
        setHomeStats(merged);
      })
      .catch((error) => {
        loader.resetOnZero();
        const errorCode = error.response?.data?.error;
        console.log("home stats error", errorCode, error);
      });

    apiGetStaffStatsHome(admin, home_uuid, year)
      .then((response) => {
        loader.resetOnZero();
        const [merged, staffmap] = processStaffMonthlyStats(response.data);
        setStaffStatsHome(merged);
        setStaffMap(staffmap);
      })
      .catch((error) => {
        loader.resetOnZero();
        const errorCode = error.response?.data?.error;
        console.log(errorCode);
      });
  };

  const actionLoadUserStats = (user_uuid: string, home_uuid: string) => {
    if (!account) return;
    loader.setLoading(3);

    apiGetUserStats(admin, user_uuid, year)
      .then((response) => {
        console.log("user stats response", response.data);
        loader.resetOnZero();
        const [objectives, activitymap] = processActivityStats(response.data);
        console.log("user stats objectives", objectives);
        setObjectives(objectives);
        setActivityMap(activitymap);
      })
      .catch((error) => {
        loader.resetOnZero();
        const errorCode = error.response?.data?.error;
        console.log("user stats error", errorCode, error);
      });

    apiGetStaffStatsUser(admin, home_uuid, user_uuid, year)
      .then((response) => {
        loader.resetOnZero();
        const [merged] = processStaffMonthlyStats(response.data); //skipping staffmap
        setStaffStatsUser(merged);
      })
      .catch((error) => {
        loader.resetOnZero();
        const errorCode = error.response?.data?.error;
        console.log(errorCode);
      });

    apiGetUserCalendarHistory(admin, user_uuid, year)
      .then((response) => {
        loader.resetOnZero();
        setUserCalendarHistory(response.data);
      })
      .catch((error) => {
        loader.resetOnZero();
        const errorCode = error.response?.data?.error;
        console.log(errorCode);
      });

    apiGetWellbeingHistory(admin, user_uuid, year)
      .then((response) => {
        loader.resetOnZero();
        const [calendar, summary] = processUserWellbeing(response.data);
        setUserWellbeing(calendar);
        setUserWellbeingYearSummary(summary);
      })
      .catch((error) => {
        loader.resetOnZero();
        const errorCode = error.response?.data?.error;
        console.log(errorCode);
      });
  };

  /////////////////////////////////////////////////////////////////////////

  const renderOverviewCards = (summary: Stats) => {
    if (!summary) return null;

    const items = [
      {
        label: _("stats", "total_activities") || "Scheduled activities",
        value: summary.count_total ?? 0,
      },
      {
        label: _("stats", "box_performed") || "Performed",
        value: summary.count_performed ?? 0,
      },
      {
        label: _("stats", "box_unable") || "Unable",
        value: summary.count_unable_to_perform ?? 0,
      },
      {
        label: _("stats", "avg_completion_rate") || "Avg completion",
        value: `${summary.percent_performed ?? 0}%`,
      },
    ];

    return (
      <Box className={css.overviewCards}>
        {items.map((item) => (
          <Box key={item.label} className={css.overviewCard}>
            <Typography className={css.overviewCardLabel}>
              {item.label}
            </Typography>
            <Typography className={css.overviewCardValue}>
              {item.value}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const showSelectOrganization =
    admin?.is_super_admin && globalAppMode === "HEALTH";
  const hasActiveAccount = account?.uuid;

  const month_name = _ar("general", "month_names");

  if (!stats) {
    return (
      <div className={cssLayout.screen}>
        <div className={css.stats}>
          <PageHeader
            title={_("stats", "main_title")}
            subtitle={_("stats", "statistics_subtitle")}
          />

          {showSelectOrganization && (
            <Box mb={3} mt={2}>
              <Typography className={css.sectionLabel} sx={{ mb: 1 }}>
                {_("stats", "select_organization")}
              </Typography>
              <Box className={css.organizationSelector}>
                {orgLoading && <CircularProgress size={20} />}
                {orgError && <Alert severity="error">{orgError}</Alert>}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {organizations.map((org) => (
                    <Button
                      key={org.uuid}
                      variant={
                        account?.uuid === org.uuid ? "contained" : "outlined"
                      }
                      color={account?.uuid === org.uuid ? "primary" : "inherit"}
                      onClick={() => setAccount(org as any)}
                    >
                      {org.name}
                    </Button>
                  ))}
                </Box>
              </Box>
            </Box>
          )}

          {!hasActiveAccount && (
            <Alert severity="info" className={css.infoAlert}>
              {_("stats", "select_organization_to_view")}
            </Alert>
          )}

          {hasActiveAccount && (
            <div className={css.emptyState}>{_("stats", "loading_stats")}</div>
          )}
        </div>
      </div>
    );
  }

  const periodString =
    period < 0 ? _("stats", "whole_year") : month_name[period];

  const periodSummary = period < 0 ? stats.summary : stats.months[period];

  const periodHome =
    homeStats && (period < 0 ? homeStats.summary : homeStats.months);

  const periodUser =
    selectedUser &&
    (period < 0 ? selectedUser.summary : selectedUser.months[period]);

  let legendIndex = -1;

  return (
    <div className={cssLayout.screen}>
      <div className={css.stats}>
        <PageHeader
          title={_("stats", "main_title")}
          subtitle={_("stats", "statistics_subtitle")}
        />

        {showSelectOrganization && (
          <Box mb={3} mt={2}>
            <Typography className={css.sectionLabel} sx={{ mb: 1 }}>
              {_("stats", "select_organization")}
            </Typography>
            <Box className={css.organizationSelector}>
              {orgLoading && <CircularProgress size={20} />}
              {orgError && <Alert severity="error">{orgError}</Alert>}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {organizations.map((org) => (
                  <Button
                    key={org.uuid}
                    variant={
                      account?.uuid === org.uuid ? "contained" : "outlined"
                    }
                    color={account?.uuid === org.uuid ? "primary" : "inherit"}
                    onClick={() => setAccount(org as any)}
                  >
                    {org.name}
                  </Button>
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {hasActiveAccount && (
          <Box mt={3} mb={1}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab
                label={
                  _("sidemenu", "health_sub_organisations") || "Accommodations"
                }
              />
              <Tab label={_("sidemenu", "users") || "Residents"} />
            </Tabs>
          </Box>
        )}

        {/* HEADER CONTROLS (Common for both tabs) */}
        <Box className={css.mainHeading} mt={2}>
          <Box className={css.dropdownWrapper}>
            <FormControl className={css.dropdown}>
              <InputLabel id="year-label">{_("stats", "year")}</InputLabel>
              <Select
                labelId="year-label"
                value={year}
                label={_("stats", "year")}
                onChange={(event) => setYear(event.target.value as number)}
                size="small"
              >
                {[2022, 2023, 2024, 2025, 2026, 2027, 2028, 2029, 2030].map(
                  (y) => {
                    if (y > new Date().getFullYear()) return undefined;
                    return (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    );
                  },
                )}
              </Select>
            </FormControl>
          </Box>
          <Box className={css.dropdownWrapper}>
            <FormControl className={css.dropdown}>
              <InputLabel id="period-label">{_("stats", "period")}</InputLabel>
              <Select
                labelId="period-label"
                value={period}
                label={_("stats", "period")}
                onChange={(event) => setPeriod(event.target.value as number)}
                size="small"
              >
                <MenuItem key={-1} value={-1}>
                  {_("stats", "whole_year")}
                </MenuItem>
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((m) => {
                  if (stats.months[m].count_total > 0) {
                    return (
                      <MenuItem key={m} value={m}>
                        {month_name[m]}
                      </MenuItem>
                    );
                  }
                  return undefined;
                })}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {activeTab === 0 && (
          <Box mt={2}>
            {/* Organisation Overview */}
            {!selectedHome && (
              <>
                <Typography variant="h6" className={css.sectionLabel} mb={2}>
                  {account?.name || ""} - {_("stats", "overview") || "Overview"}
                </Typography>
                {renderOverviewCards(periodSummary as Stats)}
                <Box className={css.section} mt={4}>
                  <TwoGraph
                    period={periodString}
                    summary={periodSummary as Stats}
                    months={stats.months}
                  />
                </Box>

                <Typography
                  variant="h6"
                  className={css.sectionLabel}
                  mt={4}
                  mb={2}
                >
                  {_("sidemenu", "health_sub_organisations") ||
                    "Accommodations"}
                </Typography>
                <Box className={css.boxes}>
                  {stats.stats.map((item) => {
                    const home = item as HomeStats;
                    return (
                      <BoxPercent
                        key={"home_" + home.home.uuid}
                        id={home.home.uuid}
                        name={home.home.name}
                        item={home}
                        period={period}
                        fnClick={() => {
                          setSelectedHome(home);
                          setActiveTab(1); // Auto switch to individual tab to see users
                        }}
                      />
                    );
                  })}
                </Box>
              </>
            )}

            {selectedHome && (
              <Box>
                <Box display="flex" alignItems="center" mb={2}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setSelectedHome(undefined)}
                    sx={{ mr: 2 }}
                  >
                    {_("general", "btn_back")}
                  </Button>
                  <Typography variant="h6" className={css.sectionLabel}>
                    {selectedHome.home.name}
                  </Typography>
                </Box>
                {periodHome && (
                  <>
                    {renderOverviewCards(periodHome as Stats)}
                    <Box className={css.section} mt={4}>
                      <TwoGraph
                        period={periodString}
                        summary={periodHome as Stats}
                        months={selectedHome.months}
                      />
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>
        )}

        {activeTab === 1 && (
          <Box mt={2}>
            <Box className={css.selectorRow}>
              <Box sx={{ minWidth: 250 }}>
                <Typography className={css.sectionLabel}>
                  {_("sidemenu", "health_sub_organisations") || "Accommodation"}
                </Typography>
                <Autocomplete
                  options={stats.stats as HomeStats[]}
                  getOptionLabel={(option) => option.home.name}
                  value={selectedHome || null}
                  onChange={(e, newValue) => {
                    setSelectedHome(newValue || undefined);
                    setSelectedUser(undefined);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} size="small" />
                  )}
                />
              </Box>

              {homeStats && (
                <Box sx={{ minWidth: 250 }}>
                  <Typography className={css.sectionLabel}>
                    {_("sidemenu", "users") || "Resident"}
                  </Typography>
                  <Autocomplete
                    options={homeStats.stats as UserStats[]}
                    getOptionLabel={(option) => option.user.identifier}
                    value={selectedUser || null}
                    onChange={(e, newValue) =>
                      setSelectedUser(newValue || undefined)
                    }
                    renderInput={(params) => (
                      <TextField {...params} size="small" />
                    )}
                  />
                </Box>
              )}
            </Box>

            {!selectedUser && (
              <Box mt={4}>
                {homeStats && (
                  <>
                    <Typography
                      variant="h6"
                      className={css.sectionLabel}
                      mb={2}
                    >
                      {_("stats", "users")}
                    </Typography>
                    <Box className={css.boxes}>
                      {homeStats.stats.map((item) => {
                        const user = item as UserStats;
                        return (
                          <BoxPercent
                            key={"usr_" + user.user.uuid}
                            id={user.user.uuid}
                            name={user.user.identifier}
                            item={user}
                            period={period}
                            fnClick={() => setSelectedUser(user)}
                          />
                        );
                      })}
                    </Box>
                  </>
                )}
                {staffStatsHome && (
                  <>
                    <Typography
                      variant="h6"
                      className={css.sectionLabel}
                      mt={4}
                      mb={2}
                    >
                      {_("stats", "staff")}
                    </Typography>
                    <Box className={css.boxes}>
                      {staffStatsHome.stats.map((item) => {
                        const staff = item as StaffSignOffStats;
                        return (
                          <BoxSignOff
                            key={staff.staff_uuid}
                            staff={staff}
                            period={period}
                          />
                        );
                      })}
                    </Box>
                  </>
                )}
              </Box>
            )}

            {selectedUser && (
              <Box mt={2}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setSelectedUser(undefined)}
                    sx={{ mr: 2 }}
                  >
                    {_("general", "btn_back")}
                  </Button>
                  <Typography variant="h6" className={css.sectionLabel}>
                    {selectedUser.user.identifier}
                  </Typography>
                </Box>

                {periodUser && (
                  <>
                    {renderOverviewCards(periodUser as Stats)}
                    <Box className={css.section} mt={4}>
                      <TwoGraph
                        period={periodString}
                        summary={periodUser as Stats}
                        months={selectedUser.months}
                      />
                    </Box>
                  </>
                )}

                {userWellbeing && userWellbeingYearSummary && (
                  <Box className={css.section}>
                    <Typography
                      variant="h6"
                      className={css.sectionLabel}
                      mb={2}
                    >
                      {_("stats", "wellbeing")}
                    </Typography>
                    <Wellbeing
                      {...props}
                      year={year}
                      month={period}
                      data={userWellbeing}
                      yearSummary={userWellbeingYearSummary}
                    />
                  </Box>
                )}

                {staffStatsUser && (
                  <Box mt={4}>
                    <Typography
                      variant="h6"
                      className={css.sectionLabel}
                      mb={2}
                    >
                      {_("stats", "staff")}
                    </Typography>
                    <Box className={css.boxes}>
                      {staffStatsUser.stats.map((item) => {
                        const staff = item as StaffSignOffStats;
                        return (
                          <BoxSignOff
                            key={staff.staff_uuid}
                            staff={staff}
                            period={period}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {objectives && (
                  <Box mt={4}>
                    <Typography
                      variant="h6"
                      className={css.sectionLabel}
                      mb={2}
                    >
                      {_("stats", "objectives")}
                    </Typography>
                    <Box className={css.boxes}>
                      {objectives.map((obj) => (
                        <BoxPercent
                          key={"obj_" + obj.id}
                          id={obj.id.toString()}
                          name={obj.title}
                          item={obj}
                          period={period}
                          fnClick={() => setSelectedObjective(obj)}
                          style={{
                            backgroundColor: getObjectiveColor(obj.color),
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {selectedObjective && (
                  <Box className={css.section} mt={4}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setSelectedObjective(undefined)}
                        sx={{ mr: 2 }}
                      >
                        {_("general", "btn_close")}
                      </Button>
                      <Typography variant="h6" className={css.sectionLabel}>
                        {selectedObjective.title}
                      </Typography>
                    </Box>
                    {barChartData && (
                      <Box className={css.chartContainer} sx={{ height: 400 }}>
                        <Bar
                          ref={ref}
                          data={barChartData}
                          options={barChartOptions}
                        />
                      </Box>
                    )}
                    <Box className={css.legendContainer}>
                      {selectedObjective.stats.map((t) => {
                        const item = t as ActivityStats;
                        const count =
                          period < 0
                            ? item.summary.count_total
                            : item.months[period].count_total;
                        if (count === undefined) return null;
                        legendIndex++;
                        return (
                          <Box
                            key={"legend-" + legendIndex}
                            className={css.legend}
                          >
                            <Box
                              className={css.dot}
                              sx={{
                                backgroundColor:
                                  COLOR_PALETTE[
                                    legendIndex % COLOR_PALETTE.length
                                  ],
                              }}
                            />
                            <Typography className={css.text}>
                              {item.activity_title}
                            </Typography>
                            <Typography className={css.count}>
                              {count}x
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {userCalendarHistory && (
                  <Box mt={4}>
                    <Typography
                      variant="h6"
                      className={css.sectionLabel}
                      mb={2}
                    >
                      {_("stats", "activites")}
                      {selectedObjective && ` (${selectedObjective.title})`}
                    </Typography>
                    <ActivityTable
                      staffMap={staffMap}
                      activityMap={activityMap}
                      period={period}
                      data={userCalendarHistory}
                      objectiveId={selectedObjective?.id}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </div>
    </div>
  );
};

const barChartOptions = {
  scales: {
    x: { stacked: true },
    y: {
      min: 0,
      ticks: { stepSize: 1 },
      stacked: true,
    },
  },
  maxBarThickness: 40,
  maintainAspectRatio: false,
  animation: { duration: 0 },
};

export default StatsScreen;
